/**
 * iframe 宿主来源配置，供构建与运行时共用。
 *
 * 单独成文件且**零依赖**：既被 widget 运行时配置（src/config.ts）使用，
 * 也被 vite 配置（vite.config.ts 里给 HTML 加 frame-ancestors CSP）使用。
 * 一旦这里引入任何 workspace 包，vite 的配置加载器就需要在 Node 里解析 TS 源码，
 * 因此这里只使用原生 URL，不引入 workspace 包。
 *
 * 约束来源：CONTRACT.md 第 60 行（dev identity/test-host 固定跑在本地 25175），
 * 默认使用精确来源；只有显式单独配置 '*' 才开启任意 HTTP/HTTPS 宿主。
 */

/** 本地宿主默认来源：独立测试页 25175 与 v3 开发页 9000/9001，不是通配。 */
export const DEFAULT_HOST_ORIGINS: readonly string[] = [
  'http://127.0.0.1:25175',
  'http://localhost:25175',
  'http://127.0.0.1:9000',
  'http://localhost:9000',
  'http://127.0.0.1:9001',
  'http://localhost:9001',
];

/** 本地后端入口端口（CONTRACT.md 第 60 行）：API 与 WebSocket 共用 28080。 */
export const DEFAULT_API_BASE_URL = 'http://127.0.0.1:28080';
export const DEFAULT_WS_BASE_URL = 'http://127.0.0.1:28080';

/** 单独的 '*' 或精确 http(s) origin 列表；拒绝路径、子域通配与混用。 */
export function parseHostOrigins(raw: string): string[] {
  const entries = raw.split(',').map((entry) => entry.trim()).filter(Boolean);
  if (entries.length === 1 && entries[0] === '*') return ['*'];
  if (entries.length === 0) throw new Error('宿主来源白名单不能为空');
  const origins = new Set<string>();
  for (const entry of entries) {
    if (entry.includes('*')) {
      throw new Error('宿主来源必须是精确 origin，或单独的通配符 *');
    }
    let url: URL;
    try {
      url = new URL(entry);
    } catch {
      throw new Error('宿主来源必须是精确的 http(s) origin');
    }
    if (!['http:', 'https:'].includes(url.protocol) ||
        (entry !== url.origin && entry !== `${url.origin}/`)) {
      throw new Error('宿主来源必须是精确 origin（不能带路径、查询或凭据）');
    }
    origins.add(url.origin);
  }
  return [...origins];
}

export function frameAncestorsPolicy(raw?: string): string {
  const origins = raw === undefined ? [...DEFAULT_HOST_ORIGINS] : parseHostOrigins(raw);
  return `frame-ancestors ${origins.join(' ')}`;
}
